import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, CartAddEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import { ADD_TO_CART_TEXT_ANIMATION_DURATION } from '@theme/product-form';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {AbortController | null} */
  #cartEventAbortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {number | undefined} */
  #animationTimeout;
  /** @type {number | undefined} */
  #cleanupTimeout;

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const productLink = productCard?.getProductCardLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    
    // Listen for cart add events to show success state and trigger fly-to-cart animation
    this.#cartEventAbortController = new AbortController();
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAdd, { 
      signal: this.#cartEventAbortController.signal 
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartEventAbortController?.abort();
    
    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    // Get the product form component and form
    const productFormComponent = this.querySelector('product-form-component');
    if (!productFormComponent) return;

    const form = productFormComponent.querySelector('form');
    if (!form) return;

    // Get the variant ID from the form
    const variantIdInput = form.querySelector('input[name="id"]');
    if (!(variantIdInput instanceof HTMLInputElement)) return;

    const variantId = variantIdInput.value;
    if (!variantId || variantIdInput.disabled) {
      // If no variant selected or variant is unavailable, open modal instead
      const currentUrl = this.productPageUrl;

      // Check if we have cached content for this URL
      let productGrid = this.#cachedContent.get(currentUrl);

      if (!productGrid) {
        // Fetch and cache the content
        const html = await this.fetchProductPage(currentUrl);
        if (html) {
          const gridElement = html.querySelector('[data-product-grid-content]');
          if (gridElement) {
            // Cache the cloned element to avoid modifying the original
            productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
            this.#cachedContent.set(currentUrl, productGrid);
          }
        }
      }

      if (productGrid) {
        // Use a fresh clone from the cache
        const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
        await this.updateQuickAddModal(freshContent);
      }

      // this.#openQuickAddModal();
      return;
    }

    // Ensure quantity is set to 1
    let quantityInput = form.querySelector('input[name="quantity"]');
    if (!quantityInput) {
      const newQuantityInput = document.createElement('input');
      newQuantityInput.type = 'hidden';
      newQuantityInput.name = 'quantity';
      form.appendChild(newQuantityInput);
      quantityInput = newQuantityInput;
    }
    if (quantityInput instanceof HTMLInputElement) {
      quantityInput.value = '1';
    }

    // Submit the form directly to add to cart
    const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
    form.dispatchEvent(submitEvent);
  };

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }

  /**
   * Handles cart add events to show success state and trigger animations
   * @param {CartAddEvent} event - The cart add event
   */
  #handleCartAdd = (event) => {
    // Only handle events from this component's product form
    const productFormComponent = this.querySelector('product-form-component');
    if (!productFormComponent) return;
    
    // Check if the event is from this component's product form
    // The event bubbles, so we check multiple ways to ensure it's from this component
    const eventSourceId = event.detail?.sourceId;
    const productFormId = productFormComponent.id;
    const eventProductId = event.detail?.data?.productId;
    const componentProductId = productFormComponent instanceof HTMLElement 
      ? productFormComponent.dataset.productId 
      : null;
    
    // Check if event came from within this component
    const eventPath = event.composedPath();
    const eventTarget = event.target;
    
    const isFromThisComponent = 
      // Check if product form is in the event path
      eventPath.includes(productFormComponent) ||
      // Check if event target is the product form
      eventTarget === productFormComponent ||
      // Check if sourceId matches
      (eventSourceId && productFormId && eventSourceId === productFormId) ||
      // Check if product ID matches (additional safety check)
      (eventProductId && componentProductId && eventProductId === componentProductId);
    
    if (!isFromThisComponent) return;
    
    // Only handle successful additions
    if (event.detail?.data?.didError) return;
    
    // Only handle if source is product-form-component (quick add uses product form)
    if (event.detail?.data?.source !== 'product-form-component') return;
    
    // Get the quick add button (for multi-variant products)
    const quickAddButton = this.querySelector('button[ref="quickAddButton"]');
    
    // For single-variant products, the button is inside add-to-cart-component
    // Those are already handled by AddToCartComponent, so we only need to handle multi-variant
    if (!(quickAddButton instanceof HTMLButtonElement)) return;
    
    // Show "added" state
    this.#animateAddToCart(quickAddButton);
    
    // Trigger fly-to-cart animation if enabled
    // Check both the quick-add component and any add-to-cart-component for the setting
    const addToCartComponent = document.querySelector('add-to-cart-component');
    const addToCartAnimation = 
      this.dataset.addToCartAnimation === 'true' ||
      (addToCartComponent instanceof HTMLElement && addToCartComponent.dataset.addToCartAnimation === 'true');
    
    if (addToCartAnimation) {
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        this.#animateFlyToCart(quickAddButton);
      });
    }
  };

  /**
   * Animates the add to cart button to show "added" state
   * @param {HTMLButtonElement} button - The button to animate
   */
  #animateAddToCart(button) {
    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!button.classList.contains('atc-added')) {
      button.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        button.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }

  /**
   * Handles variant update events to update product variant media
   * @param {import('@theme/events').VariantUpdateEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    // Update the product variant media when variant changes
    if (event.detail?.resource?.featured_media?.preview_image?.src) {
      const productVariantMedia = event.detail.resource.featured_media.preview_image.src + '&width=100';
      this.dataset.productVariantMedia = productVariantMedia;
    }
  };

  /**
   * Animates the fly to cart animation
   * @param {HTMLButtonElement} button - The button that triggered the add to cart
   */
  #animateFlyToCart(button) {
    const cartIcon = document.querySelector('.header-actions__cart-icon');
    if (!cartIcon || !button) return;

    // Get product variant media from the quick-add component's data attribute
    let image = this.dataset.productVariantMedia;

    // If not found, try to get from product form's add-to-cart-component (for single variant)
    if (!image) {
      const productFormComponent = this.querySelector('product-form-component');
      const addToCartComponent = productFormComponent?.querySelector('add-to-cart-component');
      if (addToCartComponent instanceof HTMLElement && addToCartComponent.dataset.productVariantMedia) {
        image = addToCartComponent.dataset.productVariantMedia;
      }
    }

    // Fallback: try to get from product card's featured image
    if (!image) {
      const productCardLink = this.closest('product-card-link');
      if (productCardLink instanceof HTMLElement && productCardLink.dataset.featuredMediaUrl) {
        image = productCardLink.dataset.featuredMediaUrl + '&width=100';
      }
    }

    if (!image) {
      console.warn('QuickAdd: Could not find product variant media for fly-to-cart animation');
      return;
    }

    // Create fly-to-cart element (FlyToCart is defined in product-form.js)
    const flyToCartElement = /** @type {HTMLElement & { source: Element; destination: Element }} */ (
      document.createElement('fly-to-cart')
    );

    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = button;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
